import type { TypesenseCollectionSchema } from '../../types/typesense';
import { TYPESENSE_SRP_INCLUDE_FIELDS } from './typesense-srp-include-fields';

/**
 * Profile/ranking parity fields added to the companies collection so the
 * best-companies rankings and company profile page can read from Typesense.
 * All optional (added to a populated collection via schema migration).
 * Shared with companyProfileFieldsMigration to avoid drift.
 */
export const TYPESENSE_COMPANY_PROFILE_FIELDS = [
  // Employer score + sub-scores (employerScore must stay indexed → sortable for rankings)
  { name: 'employerScore', type: 'float', optional: true },
  { name: 'payScore', type: 'float', optional: true },
  { name: 'flexibilityScore', type: 'float', optional: true },
  { name: 'stabilityScore', type: 'float', optional: true },
  { name: 'growthScore', type: 'float', optional: true },
  { name: 'benefitsScore', type: 'float', optional: true },
  { name: 'transparencyScore', type: 'float', optional: true },
  // Derived signals
  { name: 'hiringTrend', type: 'string', facet: true, optional: true },
  { name: 'remotenessRemotePct', type: 'int32', optional: true },
  { name: 'remotenessHybridPct', type: 'int32', optional: true },
  { name: 'recentJobsPerMonth', type: 'float', optional: true },
  { name: 'topRoles', type: 'string[]', optional: true, index: false },
  { name: 'companyAgeYears', type: 'float', optional: true },
  // Salary stats
  { name: 'medianSalaryMin', type: 'int32', optional: true },
  { name: 'medianSalaryMax', type: 'int32', optional: true },
  { name: 'salaryPercentile25', type: 'int32', optional: true },
  { name: 'salaryPercentile75', type: 'int32', optional: true },
  { name: 'salarySampleSize', type: 'int32', optional: true },
  // Trilingual industry (industry === industryEn)
  { name: 'industryFi', type: 'string', facet: true, optional: true },
  { name: 'industrySv', type: 'string', facet: true, optional: true },
  // Registry
  { name: 'registryCountry', type: 'string', facet: true, optional: true },
  { name: 'registryBusinessId', type: 'string', optional: true, index: false },
  { name: 'registryRegistrationDate', type: 'string', optional: true, index: false },
  { name: 'registryStatus', type: 'string', optional: true, index: false },
  // Profile display
  { name: 'brandColour', type: 'string', optional: true, index: false },
  { name: 'heroImageUrl', type: 'string', optional: true, index: false },
  // Financials
  { name: 'revenue', type: 'float', optional: true, index: false },
  { name: 'profit', type: 'float', optional: true, index: false },
  { name: 'profitMargin', type: 'float', optional: true, index: false },
  // Social
  { name: 'linkedinUrl', type: 'string', optional: true, index: false },
  { name: 'facebookUrl', type: 'string', optional: true, index: false },
  { name: 'instagramUrl', type: 'string', optional: true, index: false },
  { name: 'tiktokUrl', type: 'string', optional: true, index: false },
  { name: 'youtubeUrl', type: 'string', optional: true, index: false },
  { name: 'contactEmail', type: 'string', optional: true, index: false },
  // Distributions / monthly history stored as JSON strings (parsed on read)
  { name: 'roleDistribution', type: 'string', optional: true, index: false },
  { name: 'roleLevelDistribution', type: 'string', optional: true, index: false },
  { name: 'locationDistribution', type: 'string', optional: true, index: false },
  { name: 'remotenessDistribution', type: 'string', optional: true, index: false },
  { name: 'languageRequirements', type: 'string', optional: true, index: false },
  { name: 'companyMonthlyCounts', type: 'string', optional: true, index: false },
  // JSON-stringified CommunityInsights — grounded synthesis of public web/forum discussion.
  { name: 'communityInsights', type: 'string', optional: true, index: false },
] as const;

export const TYPESENSE_COMPANIES_SCHEMA: TypesenseCollectionSchema = {
  name: 'companies',
  fields: [
    { name: 'id', type: 'string' },
    { name: 'name', type: 'string' },
    { name: 'slug', type: 'string' },
    { name: 'logo_url', type: 'string', optional: true, index: false },
    { name: 'website', type: 'string', optional: true, index: false },
    { name: 'industry', type: 'string', facet: true, optional: true },
    { name: 'size', type: 'string', facet: true, optional: true },
    { name: 'location', type: 'string', facet: true, optional: true },
    { name: 'description', type: 'string', optional: true },
    { name: 'active_job_count', type: 'int32' },
    { name: 'annualRevenueRangeMin', type: 'int32', optional: true, facet: true },
    { name: 'annualRevenueRangeMax', type: 'int32', optional: true, facet: true },
    { name: 'headcountRangeMin', type: 'int32', optional: true, facet: true },
    { name: 'headcountRangeMax', type: 'int32', optional: true, facet: true },
    { name: 'awardsAndCertifications', type: 'string[]', optional: true, facet: true },
    ...TYPESENSE_COMPANY_PROFILE_FIELDS.map((f) => ({ ...f })),
  ],
  default_sorting_field: 'active_job_count',
};

const JOB_FIELDS_BASE = [
  { name: 'id', type: 'string' },
  { name: 'objectID', type: 'string', optional: true, index: false },
  { name: 'url', type: 'string', index: false },
  { name: 'score', type: 'float' },
  { name: 'lastSeen', type: 'int64' },
  // First time the crawler saw the listing (epoch ms). Optional: docs indexed before the
  // 2026-07 migration lack it until re-seen; alert-window filters must exclude those.
  { name: 'firstSeen', type: 'int64', optional: true },
  { name: 'postedDate', type: 'int64', facet: true },
  { name: 'applicationDeadline', type: 'int64', facet: true, optional: true },
  { name: 'isActive', type: 'bool', optional: true },
  { name: 'title', type: 'string' },
  { name: 'normalisedTitle', type: 'string', facet: true, optional: true },
  { name: 'normalisedRoleLevel', type: 'string[]', facet: true, optional: true },
  { name: 'field', type: 'string[]', facet: true, optional: true },
  { name: 'specialisations', type: 'string[]', optional: true },
  { name: 'educationLevel', type: 'string[]', facet: true, optional: true },
  { name: 'minimumYearsOfExperience', type: 'int32', optional: true },
  { name: 'peopleManagementTier', type: 'string', facet: true, optional: true },
  { name: 'companyId', type: 'string', facet: true, optional: true },
  { name: 'companyName', type: 'string', optional: true, index: false },
  { name: 'logoUrl', type: 'string', optional: true, index: false },
  { name: 'companyIndustry', type: 'string[]', facet: true, optional: true },
  { name: 'brandColour', type: 'string', optional: true, index: false },
  { name: 'heroImage', type: 'string', optional: true, index: false },
  { name: 'employerOverview', type: 'string', optional: true },
  { name: 'location', type: 'string', facet: true },
  // Facetable (unlike `location`, a joined string) so a job valid in multiple cities counts
  // toward each city's facet bucket instead of forming one combined "A, B, C" bucket.
  { name: 'locationList', type: 'string[]', optional: true, facet: true },
  { name: 'geoloc', type: 'geopoint[]', optional: true },
  { name: 'remoteness', type: 'string', facet: true, optional: true },
  { name: 'isFinland', type: 'bool', facet: true, optional: true },
  { name: 'jobType', type: 'string', facet: true, optional: true },
  { name: 'description', type: 'string' },
  { name: 'requirements', type: 'string', optional: true },
  { name: 'responsibilities', type: 'string', optional: true },
  { name: 'benefits', type: 'string[]', facet: true, optional: true },
  { name: 'skillsAndTechnologies', type: 'string[]', facet: true, optional: true },
  { name: 'requiredLanguages', type: 'string[]', facet: true, optional: true },
  { name: 'optionalLanguages', type: 'string[]', facet: true, optional: true },
  { name: 'salaryRangeMin', type: 'int32', facet: true, optional: true },
  { name: 'salaryRangeMax', type: 'int32', facet: true, optional: true },
  { name: 'salaryIsEstimate', type: 'bool', optional: true, index: false },
  { name: 'salaryEstimateSampleSize', type: 'int32', optional: true, index: false },
  { name: 'salaryPeriod', type: 'string', optional: true },
  { name: 'salaryDetails', type: 'string', optional: true, index: false },
  { name: 'currency', type: 'string', optional: true, index: false },
  { name: 'originalSalaryRangeMin', type: 'int32', optional: true, index: false },
  { name: 'originalSalaryRangeMax', type: 'int32', optional: true, index: false },
  { name: 'originalSalaryPeriod', type: 'string', optional: true, index: false },
  { name: 'originalCurrency', type: 'string', optional: true, index: false },
  { name: 'isOpenApplication', type: 'bool', facet: true, optional: true },
  { name: 'isSummerJob', type: 'bool', facet: true, optional: true },
  { name: 'isDriversLicenseRequired', type: 'bool', facet: true, optional: true },
  { name: 'isTyomarkkinatori', type: 'bool', optional: true },
  { name: 'vacationWeeks', type: 'int32', facet: true, optional: true },
  { name: 'companyURL', type: 'string', optional: true, index: false },
  { name: 'locale', type: 'string', facet: true },
] as const;

function buildJobsSchema(name: 'jobs_fi' | 'jobs_sv' | 'jobs_en', locale: 'fi' | 'sv' | 'en'): TypesenseCollectionSchema {
  const localizedFields = JOB_FIELDS_BASE.map((field) => {
    if (field.name === 'title' || field.name === 'normalisedTitle' || field.name === 'description') {
      return { ...field, locale };
    }
    return { ...field };
  });

  return {
    name,
    fields: [...localizedFields],
    default_sorting_field: 'score',
    token_separators: ['-', '_', '/'],
  };
}

export const TYPESENSE_JOBS_FI_SCHEMA = buildJobsSchema('jobs_fi', 'fi');
export const TYPESENSE_JOBS_SV_SCHEMA = buildJobsSchema('jobs_sv', 'sv');
export const TYPESENSE_JOBS_EN_SCHEMA = buildJobsSchema('jobs_en', 'en');

/** Creation order: companies first, then jobs. */
export const TYPESENSE_COLLECTION_SCHEMAS: TypesenseCollectionSchema[] = [
  TYPESENSE_COMPANIES_SCHEMA,
  TYPESENSE_JOBS_FI_SCHEMA,
  TYPESENSE_JOBS_SV_SCHEMA,
  TYPESENSE_JOBS_EN_SCHEMA,
];

export const DEFAULT_TYPESENSE_SEARCH_CONFIG = {
  query_by:
    'normalisedTitle,title,normalisedRoleLevel,location,remoteness,description,jobType,benefits,companyId,specialisations,companyIndustry,field,educationLevel,skillsAndTechnologies',
  include_fields: TYPESENSE_SRP_INCLUDE_FIELDS,
  per_page: 24,
  drop_tokens_threshold: 1,
  typo_tokens_threshold: 1,
  min_len_1typo: 6,
  min_len_2typo: 12,
} as const;
